import Link from "@components/Link/Link";
import {
  TypeGeneralLinkSkeleton,
  TypeNavGroupHeadingSkeleton,
  TypeNavLinkListSkeleton,
} from "@customTypes/contentful";
import { NavLinkType } from "@customTypes/megaNav";
import { MEGA_NAV_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import { navigationGAClick } from "@utilities/navigationGAClick";
import clsx from "clsx";
import { FC, ReactNode } from "react";
import { useNavigationStateContext } from "../../context/navigationContext";
import IconChevron from "../../public/image/svg/chevron.svg";
import styles from "./MegaNav.module.scss";
import { getPrefixedUrl } from "./utils";

type Props = {
  navLink:
    | TypeNavLinkListSkeleton
    | TypeNavGroupHeadingSkeleton
    | TypeGeneralLinkSkeleton;

  children?: ReactNode;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  active: boolean;
};

const toValidId = (text: string): string => {
  return text.toLowerCase().trim().replace(/\s+/g, "-");
};

const NavLink: FC<Props> = ({
  navLink,
  onClick,
  onMouseEnter,
  onMouseLeave,
  active,
  children,
}) => {
  const { handleReset } = useNavigationStateContext();

  const navLinkType = navLink?.component;

  switch (navLinkType) {
    case NavLinkType.NavLinkList: {
      const navLinkList = navLink as TypeNavLinkListSkeleton;
      const navLinkListOwnLink = navLinkList?.fields?.ownLink;
      const url = getPrefixedUrl(navLinkListOwnLink?.fields?.url);
      const testID = `${toValidId(navLinkListOwnLink?.fields?.linkText || "")}`;
      const id = `meganav-${testID}`;

      return (
        <li
          className={clsx(styles["nav-link"], active && styles.active)}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {navLinkListOwnLink?.fields?.linkText ? (
            <>
              {!url ? (
                <button
                  className={clsx(styles["submenu-button"], {
                    "font-bold": active,
                  })}
                  onClick={onClick}
                  {...getE2EAnchorAttribute(MEGA_NAV_ANCHOR, `${testID}-link`)}
                >
                  {navLinkListOwnLink?.fields?.linkText}
                  <IconChevron aria-hidden="true" focusable="false" />
                </button>
              ) : (
                <span
                  className={clsx(styles["submenu-title"], {
                    "font-bold": active,
                  })}
                >
                  <Link
                    href={url}
                    onClick={(e) => {
                      navigationGAClick(e);
                      handleReset();
                    }}
                    id={id}
                    {...getE2EAnchorAttribute(MEGA_NAV_ANCHOR, `${url}-link`)}
                  >
                    {navLinkListOwnLink?.fields?.linkText}
                  </Link>
                  <button
                    className={styles["submenu-icon"]}
                    onClick={onClick}
                    aria-label="Open submenu"
                    aria-describedby={id}
                  >
                    <IconChevron aria-hidden="true" focusable="false" />
                  </button>
                </span>
              )}
            </>
          ) : null}
          {children}
        </li>
      );
    }

    case NavLinkType.NavGroupHeading: {
      const headingWithType = navLink as TypeNavGroupHeadingSkeleton;
      const url = headingWithType.fields?.link?.fields?.url;
      return (
        <li className={styles["nav-link"]}>
          <h4 className={styles["nav-heading"]}>
            {url ? (
              <Link
                href={url}
                onClick={(e) => {
                  if (headingWithType.fields?.link?.fields?.url) {
                    navigationGAClick(e);
                    handleReset();
                  }
                }}
              >
                {headingWithType.fields?.label ??
                  headingWithType.fields?.link?.fields?.linkText}
              </Link>
            ) : (
              <span>{headingWithType.fields?.label}</span>
            )}
          </h4>
        </li>
      );
    }

    case NavLinkType.GeneralLink: {
      const generalLink = navLink as TypeGeneralLinkSkeleton;
      const url = generalLink?.fields?.url || "#";

      return (
        <li className={styles["nav-link"]}>
          {generalLink?.fields?.linkText ? (
            <Link
              href={url}
              onClick={(e) => {
                navigationGAClick(e);
                handleReset();
              }}
              className={styles.generalLink}
              {...getE2EAnchorAttribute(MEGA_NAV_ANCHOR, `${url}-link`)}
            >
              {generalLink?.fields?.linkText}
            </Link>
          ) : null}
        </li>
      );
    }
  }
};

export default NavLink;
